import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import TinyEditor from "../../../custom-ckeditor/TinyEditor";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import ItemService from "../../../apis/ItemService";
import {
  BLOOMS_TAXONOMY_SKILLS,
  normalizeSkillTypeValue,
} from "../constants/lessonMetadata";
import { normaliseTaxonomyOptions } from "../utils/taxonomy";
import AddIcon from "@mui/icons-material/Add";
import getAuthoringPreviewProps from "./getAuthoringPreviewProps";
import ActivityRenderer from "../../items/ActivityRenderer";

const SEGMENT_TYPES = {
  TEXT: "text",
  DROPDOWN: "dropdown",
};

const createTextSegment = (id, text = "") => ({
  id,
  type: SEGMENT_TYPES.TEXT,
  text,
});

const createDropdownSegment = (id, options = [""]) => ({
  id,
  type: SEGMENT_TYPES.DROPDOWN,
  options,
});

const parseMfbValue = (value) => {
  if (!value || typeof value !== "string") return [];
  const segments = [];
  const words = value.split(" ");
  words.forEach((word, idx) => {
    if (word.includes("/")) {
      const opts = word.split("/").map((o) => o.trim());
      segments.push(createDropdownSegment(idx + 1, opts));
    } else {
      segments.push(createTextSegment(idx + 1, word));
    }
  });
  return segments;
};

const buildMfbValue = (segments) =>
  segments
    .map((s) =>
      s.type === SEGMENT_TYPES.DROPDOWN ? s.options.join("/") : s.text
    )
    .join(" ");

const normalisePointsValue = (value) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : value;
};

const MFBItemDetail = () => {
  const location = useLocation();
  const clonedItem = location.state?.clonedItem || null;
  const { itemId } = useParams();
  const isNew = !itemId || itemId === "new";
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState({
    Type: "MFB",
    Name: "",
    points: 1,
    skilltype: "",
    itemcat: "",
    Direction: "LTR",
    Options: [{ id: 1, Value: "", Rationale: "" }],
    Title: "",
  });
  const [sentence, setSentence] = useState("");
  const [segments, setSegments] = useState([]);
  const [taxonomyOptions, setTaxonomyOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isRtl, setIsRtl] = useState(false);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [effectiveItemId, setEffectiveItemId] = useState(
    clonedItem ? null : isNew ? null : itemId
  );

  // Load taxonomy once
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // 🔹 Fetch taxonomy + existing item in parallel
        const [taxonomyResponse, itemResponse] = await Promise.all([
          ItemService.fetchTaxonomy("arabic"),
          isNew ? Promise.resolve(null) : ItemService.fetchItemById(itemId),
        ]);

        // 🔹 Set taxonomy options for dropdown
        setTaxonomyOptions(normaliseTaxonomyOptions(taxonomyResponse));

        // 🔹 If editing an existing MFB item
        let loadedItem;

        if (clonedItem) {
          loadedItem = {
            ...clonedItem,
            id: undefined,
            Id: undefined,
            itemId: undefined,
            ItemId: undefined,
            PK: undefined,
            SK: undefined,
            GPK1: undefined,
            GSK1: undefined,
            json: undefined,
            Json: undefined,
            createdBy: "",
            updatedBy: "",
            version: "v1",
          };

          setEffectiveItemId(null); // FORCE create
        } else if (itemResponse?.item) {
          loadedItem = itemResponse.item;
        } else {
          loadedItem = {
            Type: "MFB",
            Name: "",
            points: 1,
            skilltype: "",
            itemcat: "",
            Direction: "LTR",
            Options: [{ id: 1, Value: "", Rationale: "" }],
            Title: "",
          };
        }

        // 🟢 Map into UI state (same code as before)
        setItem({
          Type: loadedItem.Type || "MFB",
          Name: loadedItem.Name || "",
          points: loadedItem.points ?? 1,
          skilltype: normalizeSkillTypeValue(loadedItem.skilltype) || "",
          itemcat: loadedItem.itemcat || "",
          Direction: loadedItem.Direction || "LTR",
          Options:
            loadedItem.Options && loadedItem.Options.length
              ? loadedItem.Options
              : [{ id: 1, Value: "", Rationale: "" }],
          Title: loadedItem.Title || "",
        });

        // 🟢 Extract Sentence + Segments (KEEP THIS)
        const sentenceValue = loadedItem?.Options?.[0]?.Value || "";
        setSentence(sentenceValue);
        setSegments(parseMfbValue(sentenceValue));
      } catch (err) {
        console.error("Failed to load MFB item:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [itemId, isNew]);

  // Parse sentence into segments
  const handleSentenceChange = (value) => {
    setSentence(value);
    setSegments(parseMfbValue(value));
    setItem((prev) => ({
      ...prev,
      Options: [{ ...prev.Options[0], Value: value }],
    }));
  };

  const handleSave = useCallback(async () => {
    if (!item.Name || !item.itemcat || !item.skilltype) {
      setSaveError("Please fill all required fields.");
      return;
    }

    if (!sentence.trim()) {
      setSaveError("Please enter a sentence with dropdowns using '/'.");
      return;
    }

    const normalizedPoints = normalisePointsValue(item.points);
    const timestamp = new Date().toISOString();

    const realId = effectiveItemId || itemId;

    const payload = {
      PK: `itemcat#${item.itemcat};skilltype#${item.skilltype}`,
      SK: effectiveItemId ? `item#${effectiveItemId}` : undefined,
      createdBy: "",
      updatedBy: "",
      dateCreated: timestamp,
      dateUpdated: timestamp,
      GPK1: "item",
      GSK1: effectiveItemId ? `item#${effectiveItemId}` : undefined,
      Name: item.Name,
      itemcat: item.itemcat,
      skilltype: item.skilltype,
      Options: [
        {
          id: 1,
          Rationale: item.Options[0].Rationale || "",
          Value: buildMfbValue(segments),
        },
      ],
      points: normalizedPoints,
      Title:
        item.Title ||
        "<div> Describe the picture by forming a sentence below by filling the blanks with the correct word </div>",
      Type: "MFB",
      version: "v1",
    };

    try {
      setSaving(true);
      const response = await ItemService.saveItem(
        payload,
        effectiveItemId || undefined
      );

      setSaveSuccess(true);
      setSaveError(null);

      // After first save → switch to EDIT mode
      if (!effectiveItemId && response?.item?.Id) {
        setEffectiveItemId(response.item.Id);
      }
    } catch (err) {
      setSaveError(err?.message || "Failed to save item.");
    } finally {
      setSaving(false);
    }
  }, [item, segments, sentence, effectiveItemId]);

  const jsonPreview = useMemo(() => {
    const normalizedPoints = normalisePointsValue(item.points);
    const timestamp = new Date().toISOString();
    const itemNumber = itemId || "new";

    const payload = {
      PK: `itemcat#${item.itemcat};skilltype#${item.skilltype}`,
      SK: `item#${itemNumber}`,
      createdBy: "",
      updatedBy: "",
      dateCreated: timestamp,
      dateUpdated: timestamp,
      GPK1: "item",
      GSK1: `item#${itemNumber}`,
      Name: item.Name,
      Options: [
        {
          id: 1,
          Rationale: item.Options[0].Rationale || "",
          Value: buildMfbValue(segments),
        },
      ],
      points: normalizedPoints,
      Title:
        item.Title ||
        "<div> Describe the picture by forming a sentence below by filling the blanks with the correct word </div>",
      Type: "MFB",
      version: "v1",
    };

    return JSON.stringify(payload, null, 2);
  }, [item, segments]);

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <LinearProgress />
        <Typography>Loading item details...</Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          {/* Header */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label="MFB" color="secondary" />
              <Typography variant="h5" fontWeight={600}>
                {isNew ? "Create MFB Item" : "Edit MFB Item"}
              </Typography>
            </Stack>

            {/* LEFT SIDE BUTTONS */}
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => setPreviewDialogOpen(true)}
                disabled={!segments.length}
              >
                Preview
              </Button>

              <Button
                variant="outlined"
                onClick={() => setJsonDialogOpen(true)}
                disabled={!segments.length}
              >
                View JSON
              </Button>
            </Stack>
          </Stack>

          {saveSuccess && (
            <Alert severity="success">Item saved successfully!</Alert>
          )}
          {saveError && <Alert severity="error">{saveError}</Alert>}

          {/* Basic fields */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Item Name"
                required
                fullWidth
                value={item.Name}
                onChange={(e) =>
                  setItem((p) => ({ ...p, Name: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Points"
                type="number"
                fullWidth
                required
                value={item.points}
                onChange={(e) =>
                  setItem((p) => ({ ...p, points: Number(e.target.value) }))
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth required>
                <InputLabel>Skill Type</InputLabel>
                <Select
                  value={item.skilltype}
                  label="Skill Type"
                  onChange={(e) =>
                    setItem((p) => ({
                      ...p,
                      skilltype: normalizeSkillTypeValue(e.target.value),
                    }))
                  }
                >
                  {BLOOMS_TAXONOMY_SKILLS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Item Category</InputLabel>
                <Select
                  value={item.itemcat}
                  label="Item Category"
                  onChange={(e) =>
                    setItem((p) => ({ ...p, itemcat: e.target.value }))
                  }
                >
                  {taxonomyOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* 🟢 Prompt Editor */}
          <Divider />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Instructions (Title)
            </Typography>

            <TinyEditor
              value={item.Title}
              onChange={(value) => setItem((p) => ({ ...p, Title: value }))}
              minHeight={250}
            />
          </Box>

          {/* Sentence Input */}
          <Divider />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Sentence Input
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={sentence}
              onChange={(e) => handleSentenceChange(e.target.value)}
              placeholder="Type: ذلك/هذه/هؤلاء/تلك كلبٌ وهو/وهي/وهم/وأنا/ونحن/وأنتَ ..."
            />
            <Typography variant="caption" color="text.secondary">
              Use “/” between words to create dropdowns.
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Switch checked={isRtl} onChange={() => setIsRtl((p) => !p)} />
            }
            label="Right-to-left direction"
          />
          <Dialog
            open={previewDialogOpen}
            onClose={() => setPreviewDialogOpen(false)}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle>MFB Preview</DialogTitle>

            <DialogContent dividers>
              <ActivityRenderer {...getAuthoringPreviewProps(item)} />
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Save */}
          <Stack direction="row" justifyContent="flex-end">
            <Button
              startIcon={effectiveItemId ? <SaveIcon /> : <AddIcon />}
              variant="contained"
              onClick={handleSave}
              disabled={saving}
            >
              {saving
                ? effectiveItemId
                  ? "Saving..."
                  : "Creating..."
                : effectiveItemId
                ? "Save Changes"
                : "Create"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* JSON Dialog */}
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Item JSON Preview</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              m: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "monospace",
              fontSize: 13,
            }}
          >
            {jsonPreview}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default MFBItemDetail;
